import { useState } from "react";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { getPerVisit, DEO_PER_VISIT } from "../lib/pricing";

// Shared with SkipVisitModal + JobDetailModal so a reason can't exist on one screen only.
import { SKIP_REASONS as ROUTE_SKIP_REASONS, skipReasonLabel, SKIP_NOTE_MAX } from "../lib/constants";
import { useIsMobile } from "../lib/useIsMobile";

export function RouteSkipModal({ job, clients, invoices, pricing, techId, techName, routeDate, onClose, onSkipped, showToast }) {
  const isMobile = useIsMobile();
  const [reason,  setReason]  = useState(ROUTE_SKIP_REASONS[0]);
  const [note,    setNote]    = useState("");
  const [credit,  setCredit]  = useState(false);
  const [saving,  setSaving]  = useState(false);

  const client    = clients?.find(c=>c.id===job.clientId);
  // B10: credit the rate for the ACTUAL service skipped. A skipped deodorizer
  // visit must credit the deo per-visit rate, not the (higher) cleanup rate.
  const isDeo     = job.serviceType === "deo";
  const deoFreq   = client?.deo_frequency || "biweekly";
  const perVisit  = !client ? 0
    : isDeo ? (pricing?.deoPerVisit?.[deoFreq] ?? DEO_PER_VISIT[deoFreq] ?? 0)
            : getPerVisit(client.frequency||"weekly", client.dogs||1, pricing);

  async function handleSkip() {
    setSaving(true);

    // SKIP FIRST (the status change is DB-trigger-guarded). Only post the credit
    // after the skip is confirmed, so a trigger-rejected skip can never leave a real
    // Stripe credit with no skip recorded. B2: don't touch technician_id/name here —
    // the modal isn't given the tech, so writing it would blank the real assignment.
    const skipRes = await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${job.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=representation"},body:JSON.stringify({status:"skipped",skipped_reason:reason,skip_note:note.trim()||null,credited:false})});
    if (!skipRes.ok) {
      const e = await skipRes.json().catch(()=>({}));
      showToast(`Skip failed: ${e.message||`HTTP ${skipRes.status}`}`, "error");
      setSaving(false);
      return;
    }

    // Credit is best-effort after a confirmed skip. Never guess an amount.
    let creditApplied = false;
    if (credit) {
      if (!client || !(perVisit > 0)) {
        showToast("Skipped, but credit not applied — pricing unavailable", "error");
      } else {
        try {
          const res = await fetch(`${SUPABASE_URL}/functions/v1/apply-credit`,{
            method:"POST",
            headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
            body:JSON.stringify({
              action:      "apply",
              client_id:   job.clientId,
              amount:      perVisit,
              description: `Skipped visit credit — ${routeDate}${reason?` (${skipReasonLabel(reason)})`:""}`,
              job_id:      job.id,   // real row → credit:apply idempotency prevents a double-post
            })
          });
          const body = await res.json().catch(()=>({}));
          if (!res.ok || body?.error) throw new Error(body?.error || `HTTP ${res.status}`);
          creditApplied = true;
          await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${job.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({credited:true})});
          showToast(body.idempotent ? "Skipped — a credit already existed for this visit" : `Skipped — $${perVisit.toFixed(2)} credited`);
        } catch (e) {
          console.error("Credit apply failed:", e);
          showToast(`Skipped, but credit failed: ${e.message||e}`, "error");
        }
      }
    }

    // Log history — only claim a credit was applied if it actually was.
    await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:job.clientId,event_type:"skipped_visit",description:`Visit on ${routeDate} skipped${techName?` by ${techName}`:""}${creditApplied?` (credit of $${perVisit.toFixed(2)} applied)`:credit?" — credit FAILED":""}`,new_value:skipReasonLabel(reason)+(note.trim()?` — ${note.trim()}`:"")})});
    setSaving(false);
    await onSkipped();
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      {/* Phone: span the viewport like the theme's other bottom sheets (an inline 400px cap
          beats the mobile .modal{max-width:100%} rule and leaves a gap on 390-430px phones);
          the × gets a 40px tap area. */}
      <div className="modal" style={{maxWidth: isMobile ? "100%" : 400}}>
        <div className="modal-header">
          <h3>⊘ Skip Visit</h3>
          <button className="close-btn" aria-label="Close" onClick={onClose} style={isMobile ? {padding:8,minWidth:40,minHeight:40,margin:"-8px -8px -8px 0"} : undefined}>×</button>
        </div>
        <div className="modal-body">
          <div style={{background:"#f5f6f8",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
            <div style={{fontWeight:600}}>{job.serviceType==="deo"?"💨":"🧹"} {job.firstName}</div>
            <div style={{fontSize:12,color:"#42526e"}}>{routeDate}</div>
          </div>
          <div className="form-group" style={{marginBottom:14}}>
            <label>Reason</label>
            <select value={reason} onChange={e=>setReason(e.target.value)}>
              {ROUTE_SKIP_REASONS.map(r=><option key={r} value={r}>{skipReasonLabel(r)}</option>)}
            </select>
          </div>
          <div className="form-group" style={{marginBottom:14}}>
            <label>Skip note <span style={{color:"#8993a4",fontWeight:400}}>(optional)</span></label>
            <textarea value={note} onChange={e=>setNote(e.target.value)} maxLength={SKIP_NOTE_MAX} rows={2} placeholder="Anything the office should know"/>
          </div>
          <div style={{background:"#fffae6",border:"1px solid #fde047",borderRadius:8,padding:"12px 14px"}}>
            <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={credit} onChange={e=>setCredit(e.target.checked)} style={{accentColor:NAVY,marginTop:2,width:16,height:16,flexShrink:0}}/>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>Credit the client</div>
                <div style={{fontSize:12,color:"#8993a4",marginTop:2}}>
                  Credit <strong style={{color:"#006644"}}>${perVisit.toFixed(2)}</strong> to the client's balance — applied to their next invoice.
                  Can be applied again if skipped again.
                </div>
              </div>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSkip} disabled={saving}>
            {saving?"Saving...":"Skip Visit"}
          </button>
        </div>
      </div>
    </div>
  );
}
